class CartDrawerPremium extends HTMLElement {
  constructor() {
    super();
    this.overlay = this.querySelector('[data-cart-overlay]');
    this.panel = this.querySelector('.cart-drawer-premium__panel');
    this.closeButtons = this.querySelectorAll('[data-cart-close]');
    this.lastFocusedElement = null;
    this.isOpen = false;
    this.sectionId = this.dataset.sectionId;
  }

  connectedCallback() {
    this.bindStaticEvents();
    this.bindDynamicEvents();
  }

  bindStaticEvents() {
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    this.closeButtons.forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-cart-open]');
      if (!trigger) return;
      event.preventDefault();
      this.open();
    });
  }

  bindDynamicEvents() {
    this.querySelectorAll('[data-quantity-change]').forEach((button) => {
      button.addEventListener('click', () => {
        const line = Number(button.dataset.line);
        const quantity = Math.max(0, Number(button.dataset.quantity));
        this.changeLine(line, quantity, button.closest('.cart-line-premium'));
      });
    });

    this.querySelectorAll('[data-quantity-input]').forEach((input) => {
      input.addEventListener('change', () => {
        const line = Number(input.dataset.line);
        const quantity = Math.max(0, Number(input.value));
        this.changeLine(line, quantity, input.closest('.cart-line-premium'));
      });
    });

    this.querySelectorAll('[data-remove-line]').forEach((button) => {
      button.addEventListener('click', () => {
        const line = Number(button.dataset.line);
        this.changeLine(line, 0, button.closest('.cart-line-premium'));
      });
    });

    this.querySelectorAll('[data-upsell-add]').forEach((button) => {
      button.addEventListener('click', () => this.addUpsell(button));
    });
  }

  async addUpsell(button) {
    const variantId = Number(button.dataset.variantId);
    if (!variantId) return;

    const defaultLabel = button.dataset.defaultLabel || 'Add';

    button.disabled = true;
    button.textContent = 'Adding…';

    try {
      const payload = {
        items: [{ id: variantId, quantity: 1 }],
        sections: this.sectionId,
        sections_url: window.location.pathname
      };

      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Upsell add failed');

      const data = await response.json();

      if (data.sections && data.sections[this.sectionId]) {
        this.renderFromSectionHtml(data.sections[this.sectionId]);
      } else {
        await this.refresh();
      }

      this.open();
    } catch (error) {
      console.error(error);
      button.disabled = false;
      button.textContent = defaultLabel;
    }
  }

  async changeLine(line, quantity, lineElement) {
    if (!line) return;

    const loadingNode = lineElement?.querySelector('[data-line-loading]');
    if (loadingNode) loadingNode.hidden = false;

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          line,
          quantity,
          sections: this.sectionId,
          sections_url: window.location.pathname
        })
      });

      if (!response.ok) {
        throw new Error('Cart change failed');
      }

      const data = await response.json();

      if (data.sections && data.sections[this.sectionId]) {
        this.renderFromSectionHtml(data.sections[this.sectionId]);
      } else {
        await this.refresh();
      }

      this.open();
    } catch (error) {
      console.error(error);
    } finally {
      if (loadingNode) loadingNode.hidden = true;
    }
  }

  async refresh() {
    try {
      const url = `${window.location.pathname}?sections=${encodeURIComponent(this.sectionId)}`;
      const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error('Section refresh failed');

      const data = await response.json();
      if (data[this.sectionId]) {
        this.renderFromSectionHtml(data[this.sectionId]);
      }
    } catch (error) {
      console.error(error);
    }
  }

  renderFromSectionHtml(sectionHtml) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(sectionHtml, 'text/html');
    const newRoot = doc.querySelector('cart-drawer-premium');
    if (!newRoot) return;

    this.innerHTML = newRoot.innerHTML;
    this.className = newRoot.className;

    this.overlay = this.querySelector('[data-cart-overlay]');
    this.panel = this.querySelector('.cart-drawer-premium__panel');
    this.closeButtons = this.querySelectorAll('[data-cart-close]');

    this.bindStaticEvents();
    this.bindDynamicEvents();
  }

  open() {
    this.lastFocusedElement = document.activeElement;
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-premium-lock');
    window.setTimeout(() => this.panel?.focus(), 30);
    this.isOpen = true;
  }

  close() {
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-premium-lock');
    this.isOpen = false;

    if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
      this.lastFocusedElement.focus();
    }
  }
}

if (!customElements.get('cart-drawer-premium')) {
  customElements.define('cart-drawer-premium', CartDrawerPremium);
}
